import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Hash,
  Copy,
  RefreshCw,
  Zap,
  Layers,
  CheckCircle,
  AlertCircle,
  Instagram,
  Youtube,
  Twitter,
  TrendingUp,
  Tag
} from "lucide-react";
import auth from "../../../firebase.init"; 
import toast from "react-hot-toast";

const SocialTagGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("Instagram");
  const [tagCount, setTagCount] = useState(15);
  const [strategy, setStrategy] = useState("Mixed (Trending + Niche)");
  
  // Output State
  const [generatedTags, setGeneratedTags] = useState("");

  // Options
  const platforms = [
    { name: "Instagram", icon: <Instagram size={14} /> },
    { name: "TikTok", icon: <span style={{fontSize: 12, fontWeight: 800}}>Tk</span> }, // Lucide doesn't have TikTok yet
    { name: "YouTube", icon: <Youtube size={14} /> },
    { name: "X / Twitter", icon: <Twitter size={14} /> },
    { name: "LinkedIn", icon: <span style={{fontSize: 12, fontWeight: 800}}>In</span> }
  ];

  const strategies = [
    "Mixed (Trending + Niche)",
    "High Volume / Viral",
    "Niche / Specific",
    "Location Based",
    "SEO Focused"
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allyoutube-01-02d59d78beb5.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        setApiKeys(response.data.data.map((keyObj) => keyObj.key));
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error(err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allyoutube-01-02d59d78beb5.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        }
      } catch (err) {
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are a Social Media Manager expert in SEO and viral growth.
    Generate a list of exactly ${tagCount} hashtags for ${platform}.
    
    Context/Topic: "${topic}"
    Strategy: ${strategy}

    Guidelines:
    1. Output ONLY the hashtags separated by spaces (e.g. #tag1 #tag2).
    2. Do NOT add bullet points, numbering, intro text, or explanations.
    3. Ensure tags are relevant and mix popular vs specific tags based on the strategy.
    4. If YouTube, output comma-separated tags instead of hashtags if the strategy implies video metadata. Otherwise use hashtags.`;
  };

  const generateTags = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic or keyword.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits.");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedTags("");

    try {
      // 1. Deduct Credit
      const deductResponse = await axios.post(
        `https://allyoutube-01-02d59d78beb5.herokuapp.com/api/deduct-credit`,
        { userEmail: user.email, amount: 1 }
      );

      if (!deductResponse.data.success) throw new Error("Failed to deduct credits");
      setCredits((prev) => prev - 1);

      // 2. Call AI
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 1024 },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (textResult) {
        setGeneratedTags(textResult.trim());
        toast.success("Tags generated!");
      } else {
        throw new Error("Empty response");
      }

    } catch (err) {
      console.error(err);
      toast.error(err.message);
      setError(err.message);

      // Refund Logic
      try {
        await axios.post(`https://allyoutube-01-02d59d78beb5.herokuapp.com/api/refund-credit`, {
          userEmail: user.email, amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded.");
      } catch (refundErr) { console.error(refundErr); }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS
  // ==========================================
  const copyToClipboard = () => {
    if (!generatedTags) return;
    navigator.clipboard.writeText(generatedTags).then(() => toast.success("Copied to clipboard!"));
  };

  // ==========================================
  // 5. RENDER UI
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-wrapper">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted fw-medium">Loading Tag Generator...</p>
        <style jsx>{`.loader-wrapper { height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f8fafc; }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card text-center">
          <div className="mb-4 icon-box mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <Hash size={30} />
          </div>
          <h2 className="fw-bold text-dark mb-2">Tag Generator</h2>
          <p className="text-muted mb-4">Please log in to access the tool.</p>
          <button className="btn btn-primary btn-lg w-100 fw-semibold" onClick={() => window.location.href = "/login"}>
            Sign In
          </button>
        </div>
        <style jsx>{`
          .auth-wrapper { height: 100vh; display: flex; align-items: center; justify-content: center; background: #f1f5f9; }
          .auth-card { background: white; padding: 2.5rem; border-radius: 16px; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05); max-width: 400px; width: 90%; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="app-container">
     

      {/* Navbar */}
      <nav className="navbar sticky-top navbar-expand-lg bg-white/80 backdrop-blur border-bottom">
        <div className="container-xl">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-icon btn-sm btn-light" onClick={() => window.history.back()}>&larr;</button>
            <span className="navbar-brand fw-bold d-flex align-items-center gap-2 text-slate-800">
              <div className="brand-icon"><Hash size={18} color="white" /></div>
              TagGen
            </span>
            <div className="credit-pill">
            <Zap size={14} fill="currentColor" />
            <span>{credits} Credits</span>
            {credits < 5 && <a href="/user/select-package" className="buy-btn">Add</a>}
          </div>
          </div>
          
          
        </div>
      </nav>

      <main className="container-xl py-4 py-lg-5">
        <div className="row g-4">
          
          {/* LEFT: Configuration */}
          <div className="col-lg-4">
            <div className="saas-card h-100">
              <div className="card-header-custom">
                <Layers size={18} className="text-primary" />
                <h5 className="mb-0 fw-semibold text-slate-700">Settings</h5>
              </div>

              <div className="p-4">
                {/* Topic Input */}
                <div className="mb-4">
                  <label className="form-label-custom">Post Topic / Keyword</label>
                  <textarea 
                    className="form-control form-control-custom" 
                    rows="3" 
                    placeholder="e.g. Sunset beach photography, Digital Marketing tips..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  />
                </div>

                {/* Platform Selection */}
                <div className="mb-4">
                  <label className="form-label-custom">Platform</label>
                  <div className="platform-grid">
                    {platforms.map((p) => (
                      <div 
                        key={p.name} 
                        className={`platform-item ${platform === p.name ? 'active' : ''}`}
                        onClick={() => setPlatform(p.name)}
                      >
                        {p.icon}
                        <span>{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Strategy Dropdown */}
                <div className="mb-4">
                  <label className="form-label-custom">Strategy</label>
                  <div className="input-group-custom">
                     <span className="input-icon"><TrendingUp size={16} /></span>
                     <select 
                      className="form-select form-select-custom ps-5"
                      value={strategy}
                      onChange={(e) => setStrategy(e.target.value)}
                    >
                      {strategies.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Count Slider */}
                <div className="mb-4 p-3 bg-light rounded-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label-custom mb-0">Tag Count</label>
                    <span className="badge bg-primary rounded-pill px-3">{tagCount}</span>
                  </div>
                  <input 
                    type="range" 
                    className="form-range custom-range" 
                    min="5" 
                    max="30" 
                    step="5" 
                    value={tagCount}
                    onChange={(e) => setTagCount(Number(e.target.value))}
                  />
                </div>

                {/* Generate Button */}
                <button 
                  className="btn btn-primary-gradient w-100"
                  onClick={generateTags}
                  disabled={generating || credits < 1 || !topic.trim()}
                >
                  {generating ? (
                    <>
                      <RefreshCw className="spin me-2" size={18} /> Analyzing...
                    </>
                  ) : (
                    <>
                      <Zap size={18} className="me-2" fill="currentColor" /> Generate Tags (1 Credit)
                    </>
                  )}
                </button>
                
                {error && (
                  <div className="mt-3 text-danger small bg-danger bg-opacity-10 p-2 rounded d-flex align-items-center">
                    <AlertCircle size={14} className="me-2" /> {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Output Panel */}
          <div className="col-lg-8">
            <div className="saas-card h-100 d-flex flex-column">
              <div className="card-header-custom justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <Tag size={18} className="text-primary" />
                  <h5 className="mb-0 fw-semibold text-slate-700">Generated Tags</h5>
                </div>
                {generatedTags && (
                  <button className="btn btn-sm btn-primary" onClick={copyToClipboard}>
                    <Copy size={16} className="me-1" /> Copy All
                  </button>
                )}
              </div>

              <div className="card-body p-0 flex-grow-1 position-relative bg-paper">
                {generating ? (
                  <div className="state-container">
                    <div className="spinner-grow text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}></div>
                    <h5 className="text-slate-700">Curating Hashtags...</h5>
                    <p className="text-muted small">Checking trends for {platform}</p>
                  </div>
                ) : generatedTags ? (
                  <div className="output-scroll p-4 p-md-5">
                    <div className="tag-cloud">
                      {generatedTags.split(' ').map((tag, idx) => (
                        tag.trim() && (
                          <span key={idx} className="tag-pill">
                            {tag}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="state-container opacity-50">
                    <Hash size={64} className="text-slate-300 mb-3" />
                    <h5 className="text-slate-500">No Tags Generated Yet</h5>
                    <p className="text-muted small">Select your platform and topic to start.</p>
                  </div>
                )}
              </div>

              {generatedTags && (
                <div className="card-footer-custom">
                  <span className="text-success d-flex align-items-center gap-1 small fw-medium">
                    <CheckCircle size={14} /> Optimized for {platform}
                  </span>
                  <span className="text-muted small">
                    {generatedTags.split(' ').length} Tags
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* --- INLINE CSS FOR MODERN STYLING --- */}
      <style jsx>{`
        /* VARIABLES & RESET */
        :root {
          --primary-color: #6366f1;
          --primary-hover: #4f46e5;
          --slate-50: #f8fafc;
          --slate-100: #f1f5f9;
          --slate-200: #e2e8f0;
          --slate-600: #475569;
          --slate-800: #1e293b;
        }

        .app-container {
          background-color: var(--slate-50);
          min-height: 100vh;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          color: var(--slate-600);
        }

        /* NAVBAR */
        .backdrop-blur { backdrop-filter: blur(10px); }
        .brand-icon {
          background: var(--primary-color);
          width: 32px; height: 32px;
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
        }
        .btn-icon {
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          border: 1px solid var(--slate-200);
          color: var(--slate-600);
          transition: all 0.2s;
        }
        .btn-icon:hover { background: var(--slate-100); color: var(--slate-800); }
        
        .credit-pill {
          display: flex; align-items: center; gap: 8px;
          padding: 6px 12px;
          background: #fffbeb;
          color: #b45309;
          border: 1px solid #fcd34d;
          border-radius: 99px;
          font-weight: 600;
          font-size: 0.875rem;
        }
        .buy-btn {
          background: #b45309;
          color: white;
          text-decoration: none;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
        }

        /* CARD STYLES */
        .saas-card {
          background: white;
          border: 1px solid var(--slate-200);
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          transition: transform 0.2s;
        }
        .card-header-custom {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--slate-100);
          background: white;
          display: flex; align-items: center; gap: 0.75rem;
        }
        .card-footer-custom {
          padding: 0.75rem 1.5rem;
          border-top: 1px solid var(--slate-100);
          background: #fcfcfc;
          display: flex; justify-content: space-between; align-items: center;
        }

        /* FORM CONTROLS */
        .form-label-custom {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 700;
          color: #94a3b8;
          margin-bottom: 0.5rem;
          display: block;
        }
        .form-control-custom, .form-select-custom {
          border: 1px solid var(--slate-200);
          border-radius: 10px;
          padding: 0.65rem 0.85rem;
          font-size: 0.95rem;
          background-color: #fff;
          color: var(--slate-800);
          transition: border-color 0.2s;
        }
        .form-control-custom:focus, .form-select-custom:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
          outline: none;
        }

        /* Platform Grid */
        .platform-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
          gap: 10px;
        }
        .platform-item {
          border: 1px solid var(--slate-200);
          border-radius: 10px;
          padding: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.8rem;
          color: var(--slate-600);
        }
        .platform-item:hover { background: var(--slate-50); }
        .platform-item.active {
          border-color: var(--primary-color);
          background: #eef2ff;
          color: var(--primary-color);
          font-weight: 600;
        }

        .input-group-custom { position: relative; }
        .input-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: #94a3b8; pointer-events: none; z-index: 10;
        }

        /* BUTTONS */
        .btn-primary-gradient {
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 100%);
          color: white;
          border: none;
          padding: 0.85rem;
          border-radius: 10px;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
          transition: transform 0.2s;
        }
        .btn-primary-gradient:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35);
        }
        .btn-primary-gradient:disabled {
          background: #cbd5e1;
          box-shadow: none;
          cursor: not-allowed;
        }

        /* OUTPUT AREA */
        .bg-paper {
          background-color: #ffffff;
          background-image: radial-gradient(#f1f5f9 1px, transparent 1px);
          background-size: 20px 20px;
        }
        .output-scroll {
          height: 100%;
          overflow-y: auto;
          min-height: 400px;
        }
        .state-container {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          height: 100%; min-height: 400px;
          text-align: center;
          padding: 2rem;
        }
        
        .tag-cloud {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .tag-pill {
          background: white;
          border: 1px solid var(--slate-200);
          color: var(--primary-color);
          padding: 6px 12px;
          border-radius: 99px;
          font-size: 0.95rem;
          font-weight: 500;
          box-shadow: 0 2px 4px rgba(0,0,0,0.03);
        }

        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default SocialTagGenerator;